/**
 * The progress of „Sprawdzam działkę” kept in this browser only: the steps
 * seen (with the tone and the status of their result), the steps passed over
 * with „Pomiń ten krok”, the documents ticked, and what the card „Dokończ
 * sprawdzanie” of the find page and of the map needs (the path, the parcel's
 * title, the step to open next with its number, the summary's address, when
 * the check was last opened and whether its summary was seen). The aside marks the steps and counts them; the documents keep their
 * ticks between the last step and the summary, and the count names the ticks of the list the page shows (a flat lists the documents
 * of each market, ticked apart by the market in their values). Without storage the page works
 * and remembers nothing. Steps open in several tabs share the progress: each
 * write starts from what is stored then and changes only its own page's part.
 */

var KEY_PREFIX = 'rcn-guided-check:';
/** Checks listed by „Dokończ sprawdzanie”, the most recent first. */
var RESUME_LIMIT = 3;
/** A busy answer is asked for again this long after it showed, at most RETRY_LIMIT times in a row in a tab. */
var RETRY_DELAY_MS = 3000;
var RETRY_LIMIT = 2;
var RETRY_KEY_PREFIX = 'rcn-guided-check-retry:';

function storageOf(win) {
    try {
        return win.localStorage || null;
    } catch (e) {
        return null;
    }
}

function format(template, values) {
    return String(template || '').replace(/\{(\w+)\}/g, function(match, key) {
        return values[key] !== undefined ? values[key] : match;
    });
}

function normalize(stored) {
    var next = stored.next && typeof stored.next === 'object' && typeof stored.next.url === 'string'
        ? {
            url: stored.next.url,
            towards: typeof stored.next.towards === 'string' ? stored.next.towards : '',
            number: typeof stored.next.number === 'number' ? stored.next.number : null,
        }
        : null;
    return {
        steps: stored.steps && typeof stored.steps === 'object' && !Array.isArray(stored.steps) ? stored.steps : {},
        skipped: Array.isArray(stored.skipped) ? stored.skipped : [],
        documents: Array.isArray(stored.documents) ? stored.documents : [],
        path: typeof stored.path === 'string' ? stored.path : '',
        title: typeof stored.title === 'string' ? stored.title : '',
        total: typeof stored.total === 'number' ? stored.total : 0,
        next: next,
        summary: typeof stored.summary === 'string' ? stored.summary : '',
        updated: typeof stored.updated === 'number' ? stored.updated : 0,
        finished: stored.finished === true,
    };
}

export function readProgress(storage, subject) {
    if (!storage || !subject) return normalize({});
    try {
        var stored = JSON.parse(storage.getItem(KEY_PREFIX + subject) || 'null');
        return normalize(stored && typeof stored === 'object' ? stored : {});
    } catch (e) {
        return normalize({});
    }
}

/**
 * A busy answer of a step (`data-guided-check-step-retry`, a busy nearby
 * analysis) is asked for again: the page reloads after a pause, at most
 * RETRY_LIMIT times in a row, counted in this tab's session storage. An
 * answer shown forgets the count. Without session storage nothing reloads,
 * since nothing would stop it.
 */
function retryLater(win, subject, step, busy) {
    var session = null;
    try {
        session = win.sessionStorage || null;
    } catch (e) {
        session = null;
    }
    if (!session) return false;
    var key = RETRY_KEY_PREFIX + subject + ':' + step;
    try {
        if (!busy) {
            session.removeItem(key);
            return false;
        }
        var tries = parseInt(session.getItem(key) || '0', 10) || 0;
        if (tries >= RETRY_LIMIT) return false;
        session.setItem(key, String(tries + 1));
    } catch (e) {
        return false;
    }
    win.setTimeout(function() { win.location.reload(); }, RETRY_DELAY_MS);
    return true;
}

/**
 * Says that the page asks for the answer again by itself (`retryNote`), in the
 * status line a busy step renders empty (`data-guided-check-retry-note`): a
 * live region in the page from the start, so a screen reader announces the
 * reload as well. After the last try the line stays empty, and the step's own
 * text says to open it again.
 */
function announceRetry(root, texts) {
    var note = root.querySelector('[data-guided-check-retry-note]');
    if (note && texts && texts.retryNote) note.textContent = String(texts.retryNote);
}

function writeProgress(storage, subject, progress) {
    if (!storage || !subject) return;
    try {
        storage.setItem(KEY_PREFIX + subject, JSON.stringify(progress));
    } catch (e) { /* a full or blocked storage keeps the page working */ }
}

/** The checks of one path saved in this browser, the most recent first. */
export function savedChecks(storage, path) {
    var checks = [];
    if (!storage) return checks;
    try {
        for (var i = 0; i < storage.length; i++) {
            var key = storage.key(i);
            if (typeof key !== 'string' || key.indexOf(KEY_PREFIX) !== 0) continue;
            var subject = key.slice(KEY_PREFIX.length);
            var progress = readProgress(storage, subject);
            if (progress.path === path) checks.push({ subject: subject, progress: progress });
        }
    } catch (e) {
        return [];
    }
    return checks.sort(function(a, b) { return b.progress.updated - a.progress.updated; });
}

/** Forgets every check of one path and every entry of the guided check no page can read any more. */
function forgetChecks(storage, path) {
    if (!storage) return;
    try {
        var keys = [];
        for (var i = 0; i < storage.length; i++) {
            var key = storage.key(i);
            if (typeof key !== 'string' || key.indexOf(KEY_PREFIX) !== 0) continue;
            var stored = null;
            try {
                stored = JSON.parse(storage.getItem(key) || 'null');
            } catch (e) {
                stored = null;
            }
            if (!stored || typeof stored !== 'object' || stored.path === path) keys.push(key);
        }
        keys.forEach(function(key) { storage.removeItem(key); });
    } catch (e) { /* what was not removed stays saved */ }
}

/** The first step neither seen nor passed over, else the summary: where „Dokończ sprawdzanie” leads. */
function nextTarget(root, links, progress) {
    for (var i = 0; i < links.length; i++) {
        var id = links[i].getAttribute('data-guided-check-step-link');
        if (!progress.steps[id] && progress.skipped.indexOf(id) === -1) {
            return {
                url: links[i].getAttribute('href') || '',
                towards: links[i].getAttribute('data-guided-check-step-towards') || '',
                number: parseInt(links[i].getAttribute('data-guided-check-step-number'), 10) || null,
            };
        }
    }
    var summary = root.querySelector('[data-guided-check-summary-link]');
    return summary ? { url: summary.getAttribute('href') || '', towards: '', number: null } : null;
}

export function initCheckProgress(root, texts, options) {
    var subject = root.getAttribute('data-guided-check-subject');
    if (!subject) return null;
    var win = (options && options.window) || window;
    var now = (options && options.now) || Date.now;
    var storage = storageOf(win);
    var progress = readProgress(storage, subject);
    var step = root.getAttribute('data-guided-check-step');
    // A busy answer is no checked step: the page asks for it again, and „Dokończ sprawdzanie” keeps leading to it.
    var retry = !!step && root.hasAttribute('data-guided-check-step-retry');
    var links = Array.prototype.slice.call(root.querySelectorAll('[data-guided-check-step-link]'));
    var steps = root.querySelector('[data-guided-check-steps]');
    var total = steps ? parseInt(steps.getAttribute('data-guided-check-steps'), 10) || links.length : links.length;

    // Another tab may have saved since this page read the progress: a change
    // starts from what is stored now and changes only this page's part.
    var save = function(change) {
        if (storage) progress = readProgress(storage, subject);
        change(progress);
        progress.path = root.getAttribute('data-guided-check') || progress.path;
        progress.title = root.getAttribute('data-guided-check-subject-title') || progress.title;
        progress.total = total;
        progress.next = nextTarget(root, links, progress);
        var summary = root.querySelector('[data-guided-check-summary-link]');
        if (summary && summary.getAttribute('href')) progress.summary = summary.getAttribute('href');
        progress.updated = now();
        writeProgress(storage, subject, progress);
    };

    save(function(current) {
        if (step && !retry) {
            // A step seen is a step checked, with the result it showed.
            current.steps[step] = {
                tone: root.getAttribute('data-guided-check-step-tone') || '',
                status: root.getAttribute('data-guided-check-step-short') || '',
            };
            current.skipped = current.skipped.filter(function(id) { return id !== step; });
        }
        // A summary seen ends the check: „Dokończ sprawdzanie” no longer offers it.
        if (root.hasAttribute('data-guided-check-summary')) current.finished = true;
    });
    // A reload the visitor did not ask for is announced first.
    if (step && retryLater(win, subject, step, retry)) announceRetry(root, texts);

    var render = function() {
        var done = 0;
        links.forEach(function(link) {
            var id = link.getAttribute('data-guided-check-step-link');
            var meta = link.querySelector('[data-guided-check-step-meta]');
            var result = progress.steps[id];
            var skipped = progress.skipped.indexOf(id) !== -1;
            ['is-done', 'is-skipped', 'is-tone-success', 'is-tone-warning', 'is-tone-danger'].forEach(function(name) {
                link.classList.remove(name);
            });
            if (skipped) {
                link.classList.add('is-skipped');
                if (meta) meta.textContent = texts.skipped || '';
            } else if (result) {
                done += 1;
                link.classList.add('is-done');
                if (result.tone) link.classList.add('is-tone-' + result.tone);
                if (meta) meta.textContent = result.status || '';
            } else if (meta) {
                meta.textContent = '';
            }
        });
        var count = root.querySelector('[data-guided-check-progress-count]');
        if (count && links.length) {
            count.textContent = format(texts.progressCount, { done: done, total: total });
            count.hidden = false;
        }
        var bar = root.querySelector('[data-guided-check-progress-bar]');
        if (bar && links.length) {
            bar.setAttribute('aria-valuenow', String(done));
            if (bar.firstElementChild) bar.firstElementChild.style.width = (total > 0 ? Math.round((done / total) * 100) : 0) + '%';
            bar.hidden = false;
        }
    };
    render();

    // Every „Pomiń ten krok” of the page: the one in the step navigation and the line a phone shows under the step.
    if (step) {
        Array.prototype.forEach.call(root.querySelectorAll('[data-guided-check-skip]'), function(skip) {
            skip.addEventListener('click', function() {
                save(function(current) {
                    delete current.steps[step];
                    if (current.skipped.indexOf(step) === -1) current.skipped.push(step);
                });
            });
        });
    }

    var boxes = Array.prototype.slice.call(root.querySelectorAll('[data-guided-check-document]'));
    var documentsCount = root.querySelector('[data-guided-check-documents-count]');
    // Only the list the page shows counts: a flat keeps the documents of the other market in a hidden list.
    var countDocuments = function() {
        if (!documentsCount || !boxes.length) return;
        var shown = boxes.filter(function(box) { return !box.closest('[hidden]'); });
        var ticked = shown.filter(function(box) { return box.checked; }).length;
        documentsCount.textContent = format(texts.documentsCount, { done: ticked, total: shown.length });
        documentsCount.hidden = false;
    };
    var tickBoxes = function() {
        boxes.forEach(function(box) { box.checked = progress.documents.indexOf(box.value) !== -1; });
    };
    tickBoxes();
    boxes.forEach(function(box) {
        box.addEventListener('change', function() {
            // Only this box changes: the ticks of another tab stay.
            save(function(current) {
                var others = current.documents.filter(function(id) { return id !== box.value; });
                current.documents = box.checked ? others.concat(box.value) : others;
            });
            countDocuments();
        });
    });
    countDocuments();

    // What another tab saved shows here at once: the aside and the ticks.
    if (storage && typeof win.addEventListener === 'function') {
        win.addEventListener('storage', function(event) {
            if (event.key !== null && event.key !== KEY_PREFIX + subject) return;
            progress = readProgress(storage, subject);
            render();
            tickBoxes();
            countDocuments();
        });
    }

    // The result a page script finds in this browser for the step it shows (step 2 of a flat: where a typed price
    // falls), in place of the result the server rendered: a phrase without the visitor's numbers.
    var setStep = function(tone, status) {
        if (!step || retry) return;
        save(function(current) {
            current.steps[step] = { tone: String(tone || ''), status: String(status || '') };
            current.skipped = current.skipped.filter(function(id) { return id !== step; });
        });
        render();
    };

    // The result of a step seen earlier that a page script finds again where the page shows its answer (row 2 of the
    // summary of a flat, for the market kept in its tab), so the steps beside it say the same. Only a step seen keeps
    // it: one passed over or not yet seen stays as it is.
    var setResult = function(id, tone, status) {
        var next = { tone: String(tone || ''), status: String(status || '') };
        var seen = id ? progress.steps[id] : null;
        if (!seen || (seen.tone === next.tone && seen.status === next.status)) return;
        save(function(current) {
            if (current.steps[id]) current.steps[id] = next;
        });
        render();
    };

    // `countDocuments`: after a script shows the list of another market.
    return { progress: function() { return progress; }, render: render, countDocuments: countDocuments, setStep: setStep, setResult: setResult };
}

function fill(item, selector, text) {
    var node = item.querySelector(selector);
    if (node) node.textContent = text;
}

/**
 * „Dokończ sprawdzanie” on the find page: the unfinished checks of this path
 * saved in this browser, each a link to its next step, from the card's
 * template; „Usuń zapisane sprawdzenia” forgets every check of the path.
 */
export function initResume(root, texts, options) {
    var section = root.querySelector('[data-guided-check-resume]');
    var list = section && section.querySelector('[data-guided-check-resume-list]');
    var template = section && section.querySelector('[data-guided-check-resume-item]');
    if (!section || !list || !template || !template.content || !template.content.firstElementChild) return null;
    var win = (options && options.window) || window;
    var storage = storageOf(win);
    var base = section.getAttribute('data-guided-check-resume-base') || '';
    var path = root.getAttribute('data-guided-check') || '';
    var checks = savedChecks(storage, path).filter(function(check) {
        var next = check.progress.next;
        // Only an address of this path's steps leaves the card, whatever else the storage holds.
        return !check.progress.finished && check.progress.title !== '' && next !== null && base !== '' && next.url.indexOf(base) === 0;
    }).slice(0, RESUME_LIMIT);
    if (!checks.length) return { checks: [] };

    checks.forEach(function(check) {
        var item = template.content.firstElementChild.cloneNode(true);
        var link = item.querySelector('[data-guided-check-resume-link]');
        if (link) link.setAttribute('href', check.progress.next.url);
        fill(item, '[data-guided-check-resume-count]', format(texts.resumeCount, {
            done: Object.keys(check.progress.steps).length,
            total: check.progress.total,
        }));
        fill(item, '[data-guided-check-resume-title]', check.progress.title);
        fill(item, '[data-guided-check-resume-next]', check.progress.next.towards
            ? format(texts.resumeNext, { step: check.progress.next.towards })
            : String(texts.resumeSummary || ''));
        list.appendChild(item);
    });
    section.hidden = false;

    var clear = section.querySelector('[data-guided-check-resume-clear]');
    var cleared = section.querySelector('[data-guided-check-resume-cleared]');
    if (clear) {
        clear.addEventListener('click', function() {
            forgetChecks(storage, path);
            list.replaceChildren();
            list.hidden = true;
            clear.hidden = true;
            if (cleared) {
                // The button is gone: the note that says what happened takes the focus.
                cleared.hidden = false;
                if (typeof cleared.focus === 'function') cleared.focus();
            }
        });
    }

    return { checks: checks };
}
